import { usePathname } from 'next/navigation'
import { useRouter } from 'next/router'
import { ReactNode, createContext, useContext, useMemo } from 'react'

import { TAnyStore, TBrand, TLocale, TMarket } from 'types/common/common.types'

import { getStoreBasedOnLocale } from 'utils/common'
import { BUYFLOW_PATHS, SESSION_STORAGE_KEYS } from 'utils/constants/common'

interface LocaleContextProps {
  locale: TLocale
  market: TMarket
  brand: TBrand
}

const LocaleContext = createContext<LocaleContextProps | undefined>(undefined)

export const LocaleProvider = ({
  children,
  locale,
  market,
  brand
}: {
  children: ReactNode
  locale: TLocale
  market: TMarket
  brand: TBrand
}) => {
  return (
    <LocaleContext.Provider value={{ locale, market, brand }}>
      {children}
    </LocaleContext.Provider>
  )
}

export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}

export const useStore = () => {
  const { locale, market } = useLocale()
  const path = usePathname()
  const pathArray = path.split('/')
  const isRegistrationPage = path?.includes(BUYFLOW_PATHS[market].REGISTRATION)
  const vanityInPath = pathArray[2]

  const STORE_IN_SESSION =
    typeof window === 'undefined'
      ? ''
      : sessionStorage.getItem(SESSION_STORAGE_KEYS.STORE) || ''
  const vanity = isRegistrationPage && vanityInPath ? vanityInPath : undefined

  /**
   * TCS-6614: for abandoned cart flows and reactivation flows, these customers will come from the legacy site login page.
   * And the store will be set up in their first navigation via the store.js file, which set its final value in session storage.
   *
   * Examples are BR_REACTIVATION, AR_REACTIVATION, etc.
   *
   * (!) CORNER CASES (reported as bugs in tickets TCS-7050 and TCS-7055):
   * 1. Anonymous user comes from another site (unlikely for a regular user, common for QA) -> must update store in session to match LOCALE
   * 2. Store contains '_REACTIVATION' (comes from legacy) -> must keep store AS IS in session
   * 3. Store contains '_GRANHERMANO' (upgrade flow from free acount) -> must update store in session to be the LOCALE only
   * 4. There is a vanity in the /registrar/vanity page -> must update store in session to be LOCALE_VANITY
   */
  const LOCALE_FROM_STORE_IN_SESSION = STORE_IN_SESSION
    ? STORE_IN_SESSION.slice(0, 2)
    : ''
  const uppercaseLocale = locale.toUpperCase()
  let FINAL_STORE =
    STORE_IN_SESSION && STORE_IN_SESSION.includes('_REACTIVATION')
      ? STORE_IN_SESSION
      : uppercaseLocale

  if (STORE_IN_SESSION && STORE_IN_SESSION.includes('_GRANHERMANO')) {
    FINAL_STORE = STORE_IN_SESSION.split('_')[0]
  }

  if (LOCALE_FROM_STORE_IN_SESSION !== uppercaseLocale) {
    FINAL_STORE = uppercaseLocale
  }

  /**
   * If the store in session includes an underscore, and it's NOT  REACTIVATION or GRANHERMANO,
   * return the value saved in session.
   */
  const IS_VANITY_STORE =
    STORE_IN_SESSION &&
    STORE_IN_SESSION.includes('_') &&
    !STORE_IN_SESSION.includes('_REACTIVATION') &&
    !STORE_IN_SESSION.includes('_GRANHERMANO')

  if (IS_VANITY_STORE && (!isRegistrationPage || vanityInPath)) {
    FINAL_STORE = STORE_IN_SESSION
  }

  if (vanity && vanity !== '' && vanity !== 'false') {
    FINAL_STORE = getStoreBasedOnLocale(locale, vanity)
  }

  if (isRegistrationPage && vanityInPath) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.VANITY, vanityInPath)
  }

  sessionStorage.setItem(
    SESSION_STORAGE_KEYS.STORE,
    FINAL_STORE || uppercaseLocale
  )
  return FINAL_STORE as TAnyStore
}
